/**
 * Provide persistent storage for user experience preferences
 *
 * This is just a persistent key-value store.  Logically it should be scoped to a user, however, the current implementation
 * uses a cookie for persistence which means multiple users on the same browser will see the same settings.
 *
 * Because it uses a cookie, storage is very limited so short strings should be used.  Also, the contents are easily
 * readable by an end user.
 *
 * The interface is designed to support saving preferences to the backend so some operations are asynchronous which
 * could be synchronous with a cookie implementation.
 *
 * The loadPreferences() method should be called and completed before any other interaction with the service.  This
 * method is idempotent and will only load data once regardless of how many times it is called.  To force a reload
 * the reset() method can be used.
 *
 * loadPreferences() loads all preferences into memory for fast synchronous access.  This simplifies conditional
 * template rendering based on preference values.
 */
(function(){
  angular.module('core').factory('edxUxPreferenceService', ['edxLocalStorageService', '$q', '$log', uxPreferenceServiceFactory]);

  //
  // List known preference values
  // This list is exposed in the service interface
  // These values are serialized to a cookie, so keep them short
  //
  var registeredPreferences = {
    classroomOverviewSimplified: {
      key: 'cl.o.s',
      treatments: {
        classic: 'c',
        simplified: 's'
      }
    },
    classroomStandardsView: {
      key: 'cl.s.v',
      views: {
        grid: 'grd',
        group: 'grp'
      }
    }
  };

  var loadPromise;
  var preferences;

  function uxPreferenceServiceFactory(storageService, $q, $log) {
    /** The name of the cookie in which preferences will be stored */
    var PREFERENCE_COOKIE_NAME = 'edx-ux';

    /**
     * Gets preference value for a key
     *
     * If preferences are not yet loaded, the fallback value is returned and a warning is logged.
     *
     * @param key Preference key
     * @param fallback Value if requested preference is undefined
     * @return {*}
     */
    function getPreference(key, fallback) {
      if (isLoaded()) {
        return preferences[key] || fallback;
      } else {
        $log.warn('UX preference retrieved before load.');
        return fallback;
      }
    }

    /**
     * Load preferences from cookie into memory or initializes empty set if no cookie exists
     *
     * This must be called and allowed to complete before calling any other method.
     *
     * @return {*} Promise which resolves after preferences are loaded
     */
    function loadPreferences() {
      if (!loadPromise) {
        loadPromise = $q.when().then(function () {
          var cookie = _load();
          if (cookie) {
            preferences = angular.fromJson(cookie);
          } else {
            preferences = {};
          }
        });
      }
      return loadPromise;
    }

    /**
     * Save a new preference value
     *
     * Create or update value for a preference key
     *
     * @param key
     * @param preference
     * @return {*} Promise which resolves after preference change is persisted
     */
    function savePreference(key, preference) {
      if (isLoaded()) {
        preferences[key] = preference;
        return $q.when().then(_savePreferences);
      } else {
        $log.error('UX preferences saved before load.');
        return $q.reject({reason: 'Preference state is not loaded.'});
      }
    }

    /**
     * Tests if preferences have been loaded from storage
     *
     * @return {boolean}
     */
    function isLoaded() {
      return !!preferences;
    }

    /**
     * Resets the service to unloaded state
     *
     * This is primarily for use in testing, but can also be used to force reloading of preferences from storage
     */
    function reset() {
      loadPromise = undefined;
      preferences = undefined;
    }

    function _savePreferences() {
      _save(angular.toJson(preferences));
    }

    function _load() {
      return storageService.getItem(PREFERENCE_COOKIE_NAME);
    }

    function _save(data) {
      storageService.setItem(PREFERENCE_COOKIE_NAME, data);
    }

    return {
      savePreference: savePreference,
      getPreference: getPreference,
      loadPreferences: loadPreferences,
      isLoaded: isLoaded,
      reset: reset,
      registeredPreferences: registeredPreferences
    };
  }
})();
